import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import './App.css';
import { API_BASE } from './config';
import '@vkontakte/vkui/dist/vkui.css';
import {
  Input,
  Button
} from '@vkontakte/vkui';
import 'ol/ol.css';

// Импортируем наши компоненты
import Form1 from './components/forms/Form1';
import Form2 from './components/forms/Form2';
import Form3 from './components/forms/Form3';
import Form4 from './components/forms/Form4';
import Form5 from './components/forms/Form5';
import Form6 from './components/forms/Form6/Form6';
import { useFormData, useHandbooks, useDocuments } from './hooks';
import { LoginPanel } from './components/auth';
import { isAuthenticated, clearAuth, getUser, fetchMe } from './utils/authUtils';
import { validateAllForms, createDocument as createDocumentData, saveDocument, loadDocumentDataFromServer, transformForm1 } from './utils/documentUtils';
import { getAccessToken, getAuthHeaders } from './utils/authUtils';
import { saveDraft, deleteDraft, loadDraft } from './utils/draftUtils';
import { refToId } from './utils/handbookRef';
import { isNonForestLandCategory } from './components/forms/Form2/utils';
import DocumentsPage from './components/DocumentsPage';
import PlansPage from './components/PlansPage';
import TreeLoader from './components/common/TreeLoader';
import logoImage from './assets/images/logo_512.png';
// Руководство пользователя лежит рядом с лендингом и отдаётся с того же адреса:
// обновить его — значит заменить один файл на сервере, пересборка не нужна.
const USER_GUIDE_URL = '/app/manual/user-guide.pdf';

function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const historyInitializedRef = useRef(false);
  const isHandlingPopstateRef = useRef(false);
  const prevHasMissingRequiredFieldsRef = useRef(false);

  // Состояние авторизации
  const [authenticated, setAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  
  // Состояние для управления отображением страниц
  const [showDocumentsPage, setShowDocumentsPage] = useState(true);

  // Функция для получения пустых данных формы 1
  const getEmptyForm1Data = () => {
    return {
      // Местоположение
      lat: '',
      lon: '',
      vnum: '',
      // Сведения
      srf: '',
      filial: '',
      lvo: '',
      ulvo: '',
      urc: '',
      kv: '',
      s: '',
      sga: '',
      // Информация
      cel: '',
      data: '',
      ekolzona: '',
      kmns: false,
      zonarad: '',
      nmh: '',
      lmh: '',
      itaks1: '',
      form1ValidationErrors: true, // По умолчанию true, так как все поля пустые
      // Зависимые данные
      dependentData: {
        organizations: [],
        forestDistricts: [],
        subDistricts: [],
        toponyms: []
      }
    };
  };

  // Функция для получения пустых данных формы 2
  const getEmptyForm2Data = () => {
              return {
      // Информация
      cnl: '',             // Целевое назначение лесов
      kzl: '',             // Кат ЗЛ
      proishozdenie: '',   // Происхождение насаждения
      ozu: '',             // ОЗУ
      arenda: '',          // Право пользования
      vidarenda: '',       // Вид использования
      oopt: '',            // ООПТ
      noopt: '',           // Иное ООПТ
      glu: '',             // Год лесоустройства
      landAvailability: null, // Доступность участка (обязательно: true / false)
      // Характеристика насаждения общая
      katzem: '',          // Категория земель
      gporoda: '',         // Главная порода
      bonitet: '',         // Бонитет
      tiplesa: '',         // Тип леса
      tlu: '',             // ТЛУ
      // Характеристика насаждения по ярусам (зависимые поля)
      yarusy_dependent: [{           // Массив ярусов с зависимыми полями
        id: 1,
        yarus: '1',        // Ярус (1, 2, 3) - для связи с независимыми данными
        vysota: '',        // Высота яруса
        klass_vozrasta: '', // Класс возраста
        gruppa_vozrasta: '', // Группа возраста
        polnota: '',       // Полнота
        zapas_syro_ga: '', // Запас сырораст., кбм на га
        zapas_syro_vydel: '', // Запас сырораст., кбм на выдел
        zapas_suhostoy: '', // Запас сухостоя, кбм на выдел
        zapas_redin: '',   // Запас редин, кбм на выдел
        zapas_ed_derev: '', // Запас един. дер., кбм на выдел
        zapas_zah_obsh: '', // Запас захл. общ., кбм на выдел
        zapas_zah_likvid: '' // Запас захл. ликвид., кбм на выдел
      }],
      // Характеристика пород (зависимые поля)
      poroda_characteristics_dependent: [], // Массив характеристик пород с зависимыми полями
      form2ValidationErrors: true, // По умолчанию true, так как все поля пустые
      dependentData: {
        organizations: [],
        forestDistricts: [],
        subDistricts: [],
        toponyms: []
      }
    };
  };

  // Функция для получения пустых данных формы 3
  const getEmptyForm3Data = () => {
    return {
      // Подлесок
      podlesok: [{
        id: 1,
        sostavpdl: '',        // Породы
        gustpdl: '',          // Густота
        hpdl: '',             // Средняя высота
        sostpdl: '',          // Состояние
        reljeff: '',          // Рельеф. Форма (справочник 21)
        reljefeksp: '',       // Рельеф. Экспозиция (справочник 17)
        reljefuklon: '',      // Рельеф. Уклон
        eroziavid: '',        // Эрозия. Вид (справочник 28)
        eroziastep: '',       // Эрозия. Степень (справочник 22)
        provlhm: '',          // Проведённые хозяйственные мероприятия (справочник 32)
        itaks3: ''            // Информация пользователя
      }],
      // Подрост
      podrost: [{
        id: 1,
        sostavpod: '',        // Состав
        vozrpod: '',          // Средний возраст (целое число до 999)
        hpod: '',             // Средняя высота (дробное число 3 знака до запятой, 1 после)
        gustpod: '',          // Густота (дробное число 5 знаков до запятой, 2 после)
        sostpod: ''           // Состояние (справочник 23)
      }],
      form3ValidationErrors: true
    };
  };

  // Функция для получения пустых данных формы 4
  const getEmptyForm4Data = () => {
    return {
      // Данные таблицы
      data: [{
        id: 1,
        yarus: '',           // Ярус
        dolap: '',           // Доля
        kodporoda: '',       // Порода
        vozr: '',            // Ср. возраст, лет
        hsr: '',             // Ср. высота, м
        dsr: ''              // Ср. диаметр, см
      }],
      form4ValidationErrors: false, // По умолчанию false, так как все поля необязательные
      savedModalData: {},           // Сохраненные данные модального окна для каждой строки
      charochag: '',                // Характер усыхания насаждения
      itaks4: ''                    // Информация пользователя
    };
  };

  // Функция для получения пустых данных формы 5
  const getEmptyForm5Data = () => {
    return {
      // Данные таблицы
      data: [{
        id: 1,
        yarus: '',           // Ярус
        dolap: '',           // Доля
        kodporoda: '',       // Порода
        vozr: '',            // Ср. возраст, лет
        hsr: '',             // Ср. высота, м
        dsr: ''              // Ср. диаметр, см
      }],
      form5ValidationErrors: false, // По умолчанию false, так как все поля необязательные
      savedModalData: {},           // Сохраненные данные модального окна для каждой строки
      charochag: '',                // Характер усыхания насаждения
      itaks4: ''                    // Информация пользователя
    };
  };

  // Функция для получения пустых данных формы 6
  const getEmptyForm6Data = () => {
    return {
      // Данные таблицы
      data: [{
        id: 1,
        yarus: '',           // Ярус
        dolya: '',           // Доля
        poroda: '',          // Порода
        sksporoda: '',       // СКС
        ooporoda: '',        // Отпад (общ)
        toporoda: ''         // Отпад (тек)
      }],
      // Общие поля для всех строк
      raschetnaya_ostatochnaya_polnota: '',  // Расчётная остаточная полнота
      klass_ustoychivosti_nasazhdeniya: '',   // Класс устойчивости насаждения
      // Рекомендуемые мероприятия
      recommended_measures: [{
        id: 1,
        rekvidmer: '',             // Вид (справочник ID 25)
        srokmzl: '', // Сроки проведения
        srekme: '',         // Площадь
        primechmzl: ''      // Примечание
      }],
      form6ValidationErrors: false // По умолчанию false, так как все поля необязательные
    };
  };

  // Используем наши хуки (все хуки должны быть вызваны до любых условных возвратов)
  const { formData, setFormData } = useFormData(getEmptyForm1Data());
  const { handbooks, isLoading, error, loadHandbooks } = useHandbooks();
  const { documents, selectedDoc, setSelectedDoc, createDocument, updateDocument } = useDocuments();

  // Локальные состояния
  const [selectedForm, setSelectedForm] = useState(1);
  const [dataType, setDataType] = useState('to'); // 'to' или 'actual'
  const [selectedForm45, setSelectedForm45] = useState(4); // Выбор между формой 4 и формой 5
  // true, если форма 4/5 жёстко определена целью ВНН (form1.cel) — переключатель заблокирован
  const [form45Locked, setForm45Locked] = useState(false);

  // Состояние для модального окна предупреждения при переключении форм 4/5
  const [showFormSwitchWarning, setShowFormSwitchWarning] = useState(false);
  const [pendingFormSwitch, setPendingFormSwitch] = useState(null); // 4 или 5
  const [showFormBlockedModal, setShowFormBlockedModal] = useState(false);
  const [blockedFormMessage, setBlockedFormMessage] = useState('');
  // Проверка дубликата карточки при переходе на форму №2
  const [showForm1DuplicateLoader, setShowForm1DuplicateLoader] = useState(false);
  const [form1DuplicateWarning, setForm1DuplicateWarning] = useState(false);

  // Отслеживание несохранённых изменений карточки и подтверждение выхода в журнал
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const cardBaselineRef = useRef(null);   // снимок содержимого карточки до правок
  const cardInteractedRef = useRef(false); // было ли реальное взаимодействие пользователя
  const lastSavedSnapshotRef = useRef(null); // снимок карточки на момент последнего сохранения (для автосейва)
  const isSavingRef = useRef(false);         // защита от параллельных сохранений (ручное + автосейв)
  const autosaveTickRef = useRef(null);      // актуальная функция тика автосохранения
  // Растёт только при реальном начале новой сессии редактирования (открыли другую карточку,
  // создали новую, восстановили черновик) — в отличие от selectedDoc?.id, который тихо меняется
  // при первом автосейве новой карточки (сервер присваивает ей id) и не должен сбрасывать трекинг.
  const [editSessionId, setEditSessionId] = useState(0);

  // Состояние для сохранения документа
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  // true, пока пользователь создаёт новую карточку в этой сессии (даже после автосейва,
  // присвоившего ей UUID). Нужно для ограничения «Субъект РФ»/«Филиал» при создании.
  const [isNewCardSession, setIsNewCardSession] = useState(false);
  // Состояние для сворачивания/разворачивания панели обязательных полей
  const [isRequiredFieldsPanelCollapsed, setIsRequiredFieldsPanelCollapsed] = useState(true);

  // Состояние для хранения данных разных форм
  // eslint-disable-next-line no-unused-vars
  const [formsData, setFormsData] = useState({
    form1: getEmptyForm1Data(),
    form2: {
      to: getEmptyForm2Data(),      // Данные для "материалы ТО"
      actual: getEmptyForm2Data()   // Данные для "фактические данные"
    },
    form3: getEmptyForm3Data(),
    form4: getEmptyForm4Data(),
    form5: getEmptyForm5Data(),
    form6: getEmptyForm6Data()
  });

  // Отдельное состояние для независимых таблиц формы 2 (не зависят от переключения ТО/фактические данные)
  const [independentForm2Data, setIndependentForm2Data] = useState({
    yarusy: [{           // Массив ярусов (только независимые поля)
      id: 1,
      yarus: '1',        // Ярус (1, 2, 3)
      sostav: ''         // Состав насаждения
    }],
    poroda_characteristics: [], // Массив характеристик пород (только независимые поля)
    // Независимые зависимые данные для таблиц (одинаковые для ТО и фактических данных)
    yarusy_dependent: [{        // Массив ярусов с зависимыми полями
      id: 1,
      yarus: '1',
      vysota: '',
      klass_vozrasta: '',
      gruppa_vozrasta: '',
      polnota: '',
      zapas_syro_ga: '',
      zapas_syro_vydel: '',
      zapas_suhostoy: '',
      zapas_redin: '',
      zapas_ed_derev: '',
      zapas_zah_obsh: '',
      zapas_zah_likvid: ''
    }],
    poroda_characteristics_dependent: [] // Массив характеристик пород с зависимыми полями
  });

  // Проверка авторизации при загрузке
  useEffect(() => {
    const checkAuth = async () => {
      if (isAuthenticated()) {
        // Отправляем запрос к /me при загрузке сайта
        const meResult = await fetchMe();
        if (meResult.success) {
          setUser(meResult.user);
          setAuthenticated(true);
        } else {
          // Если запрос не удался, очищаем авторизацию
          clearAuth();
          setAuthenticated(false);
          setUser(null);
        }
      }
      setIsCheckingAuth(false);
    };
    checkAuth();
  }, []);

  // Снимок содержимого карточки для сравнения «были ли изменения»
  const getCardSnapshot = useCallback(() => {
    try {
      return JSON.stringify({ formsData, independentForm2Data, selectedForm45, dataType });
    } catch (e) {
      return null;
    }
  }, [formsData, independentForm2Data, selectedForm45, dataType]);

  // При открытии другой карточки или возврате в журнал сбрасываем отслеживание.
  // ВАЖНО: этот эффект объявлен раньше эффекта базовой линии, чтобы при входе в карточку
  // сначала отработал сброс, а затем захват базовой линии (порядок эффектов = порядок объявления).
  useEffect(() => {
    cardInteractedRef.current = false;
    cardBaselineRef.current = null;
    lastSavedSnapshotRef.current = null;
  }, [editSessionId, showDocumentsPage]);

  // Пока пользователь не начал редактировать карточку, держим базовую линию равной
  // текущему содержимому (поглощаем догрузку данных при открытии). После первого
  // реального взаимодействия базовая линия «замораживается».
  useEffect(() => {
    if (!authenticated || showDocumentsPage) {
      return;
    }
    if (!cardInteractedRef.current) {
      cardBaselineRef.current = getCardSnapshot();
      // До первой правки «последнее сохранённое» = текущее содержимое,
      // чтобы автосейв не срабатывал на догрузке данных при открытии карточки.
      lastSavedSnapshotRef.current = cardBaselineRef.current;
      // Новая (ещё не сохранённая) карточка стартует пустой — догружать нечего.
      // Сразу «замораживаем» пустую базовую линию, чтобы любое появление данных
      // (в т.ч. через модальные окна: карта, выбор субъекта и т.п.) считалось
      // несохранёнными правками и при выходе в журнал показывалась плашка.
      if (isNewCardSession) {
        cardInteractedRef.current = true;
      }
    }
  }, [authenticated, showDocumentsPage, getCardSnapshot, isNewCardSession]);

  // Раз в 2 секунды отправляем правки на сервер, если они появились после последнего
  // сохранения (тихий автосейв). Сам отбор условий — в autosaveTickRef.current,
  // который переопределяется на каждый рендер со свежим замыканием.
  useEffect(() => {
    if (!authenticated || showDocumentsPage) {
      return undefined;
    }
    const intervalId = setInterval(() => {
      if (autosaveTickRef.current) {
        autosaveTickRef.current();
      }
    }, 2000);
    return () => clearInterval(intervalId);
  }, [authenticated, showDocumentsPage]);

  // Добавляем стили для анимации спиннера
  useEffect(() => {
    const spinnerStyles = `
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `;
    
    const styleSheet = document.createElement("style");
    styleSheet.type = "text/css";
    styleSheet.innerText = spinnerStyles;
    document.head.appendChild(styleSheet);
    
    return () => {
      document.head.removeChild(styleSheet);
    };
  }, []);
  
  // Автоматическое сохранение данных формы 1 при изменении
  useEffect(() => {
    if (selectedForm === 1) {
      setFormsData(prev => ({
        ...prev,
        form1: formData
      }));
    }
  }, [formData, selectedForm]);

  // Автоматическое сохранение данных формы 2 при изменении
  // ИСКЛЮЧАЕМ независимые таблицы и их зависимые данные
  useEffect(() => {
    if (selectedForm === 2) {
      // Создаем копию formData без независимых таблиц и их зависимых данных
      const { yarusy, poroda_characteristics, yarusy_dependent, poroda_characteristics_dependent, ...dependentFormData } = formData;
      
      setFormsData(prev => ({
        ...prev,
        form2: {
          ...prev.form2,
          [dataType]: dependentFormData
        }
      }));
    }
  }, [formData, selectedForm, dataType]);

  // Автоматическое сохранение данных формы 3 при изменении
  useEffect(() => {
    if (selectedForm === 3) {
      setFormsData(prev => ({
        ...prev,
        form3: formData
      }));
    }
  }, [formData, selectedForm]);

  // Автоматическое сохранение данных формы 4 при изменении
  useEffect(() => {
    if (selectedForm === 4) {
      setFormsData(prev => ({
        ...prev,
        form4: formData
      }));
    }
  }, [formData, selectedForm]);

  // Автоматическое сохранение данных формы 4 в formsData при изменении formData
  useEffect(() => {
    if (selectedForm === 4 && selectedForm45 === 4 && formData) {
      setFormsData(prev => ({
        ...prev,
        form4: formData
      }));
    }
  }, [formData, selectedForm, selectedForm45]);

  // Автоматическое сохранение данных формы 5 в formsData при изменении formData
  useEffect(() => {
    if (selectedForm === 4 && selectedForm45 === 5 && formData) {
      setFormsData(prev => ({
        ...prev,
        form5: formData
      }));
    }
  }, [formData, selectedForm, selectedForm45]);

  // Автоматическое сохранение данных формы 6 в formsData при изменении formData
  useEffect(() => {
    if (selectedForm === 6 && formData && formData.form6) {
      setFormsData(prev => ({
        ...prev,
        form6: formData.form6
      }));
    }
  }, [formData, selectedForm]);

  // Автосохранение черновика каждые 15 секунд (только для новых отчётов)
  useEffect(() => {
    // Сохраняем черновик только если пользователь авторизован и не на странице документов
    if (!authenticated || showDocumentsPage || !user) {
      return;
    }

    // Проверяем, является ли это новым документом (не существующим)
    const isValidUUID = (id) => {
      if (!id) return false;
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
      return uuidRegex.test(id.toString());
    };
    
    // Сохраняем черновик только для новых документов (когда selectedDoc === null или id не является валидным UUID)
    const isNewDocument = !selectedDoc || !selectedDoc.id || !isValidUUID(selectedDoc.id);
    
    if (!isNewDocument) {
      // Если это существующий документ, не сохраняем черновик
      return;
    }

    const saveDraftInterval = setInterval(() => {
      try {
        // Собираем данные так же, как в handleSave (но без валидации и отправки)
        let updatedFormsData = { ...formsData };
        
        // Сохраняем данные текущей активной формы
        if (selectedForm === 1) {
          updatedFormsData.form1 = formData;
        } else if (selectedForm === 2) {
          const { yarusy, poroda_characteristics, yarusy_dependent, poroda_characteristics_dependent, ...dependentFormData } = formData;
          updatedFormsData.form2 = {
            ...updatedFormsData.form2,
            [dataType]: dependentFormData
          };
          if (formsData.form4) {
            updatedFormsData.form4 = formsData.form4;
          }
          if (formsData.form5) {
            updatedFormsData.form5 = formsData.form5;
          }
        } else if (selectedForm === 3) {
          updatedFormsData.form3 = formData;
        } else if (selectedForm === 4) {
          updatedFormsData.form4 = formData;
        } else if (selectedForm === 5) {
          updatedFormsData.form5 = formData;
        } else if (selectedForm === 6) {
          if (formData.form6) {
            updatedFormsData.form6 = formData.form6;
          }
        }
        
        // Если данные Form1 не сохранены, но formData содержит поля Form1, используем их
        if (!updatedFormsData.form1 || Object.keys(updatedFormsData.form1).length === 0) {
          if (formData.srf !== undefined || formData.filial !== undefined || formData.lvo !== undefined) {
            updatedFormsData.form1 = formData;
          }
        }
        
        // Аналогично для Form3
        if (!updatedFormsData.form3 || Object.keys(updatedFormsData.form3).length === 0) {
          if (formData.sostavpod !== undefined || formData.sostpod !== undefined || formData.reljeff !== undefined) {
            updatedFormsData.form3 = formData;
          }
        }
        
        // Получаем ID пользователя
        const userId = user?.id || 3;
        
        // Используем имя из selectedDoc, если оно есть
        const documentName = (typeof selectedDoc?.name === 'string' && selectedDoc.name.trim() !== '') 
          ? selectedDoc.name 
          : null;
        
        // Создаем документ для черновика (те же данные, что собирались для отправки)
        const document = createDocumentData(updatedFormsData, independentForm2Data, selectedForm45, userId, handbooks, documentName, dataType);
        
        // Сохраняем черновик
        saveDraft(document);
      } catch (error) {
        console.error('Ошибка при автосохранении черновика:', error);
      }
    }, 15000); // 15 секунд

    return () => {
      clearInterval(saveDraftInterval);
    };
  }, [authenticated, showDocumentsPage, user, formsData, formData, selectedForm, dataType, selectedForm45, independentForm2Data, handbooks, selectedDoc]);

  // Обработчик успешной авторизации
  const handleLoginSuccess = (userData) => {
    setUser(userData);
    setAuthenticated(true);
    // Показываем журнал карточек ВНН (список отчётов), а не документ предыдущей сессии
    setSelectedDoc(null);
    setShowDocumentsPage(true);
    navigate('/');
  };

  // Обработчик выхода
  const handleLogout = () => {
    clearAuth();
    setAuthenticated(false);
    setUser(null);
    // Сбрасываем выбранный документ и открываем журнал карточек ВНН
    setSelectedDoc(null);
    setShowDocumentsPage(true);
    navigate('/', { replace: true });
  };

  // Токен обновления истёк или отозван (сменили пароль) — возвращаемся к форме входа.
  const logoutRef = React.useRef(handleLogout);
  logoutRef.current = handleLogout;
  useEffect(() => {
    const onExpired = () => logoutRef.current();
    window.addEventListener('glpm:session-expired', onExpired);
    return () => window.removeEventListener('glpm:session-expired', onExpired);
  }, []);

  // Получаем список обязательных полей, которые не заполнены
  const getMissingRequiredFields = () => {
    const validation = validateAllForms(formsData, independentForm2Data, handbooks, dataType, selectedForm45);
    const missingFields = {
      form1: [],
      form2: [],
      form3: [],
      form4: [],
      form5: [],
      form6: []
    };
    
    validation.errors.forEach(error => {
      if (error.startsWith('Form1:')) {
        missingFields.form1.push(error.replace('Form1: ', ''));
      } else if (error.startsWith('Form2:')) {
        missingFields.form2.push(error.replace('Form2: ', ''));
      } else if (error.startsWith('Form3:')) {
        missingFields.form3.push(error.replace('Form3: ', ''));
      } else if (error.startsWith('Form4:')) {
        missingFields.form4.push(error.replace('Form4: ', ''));
      } else if (error.startsWith('Form5:')) {
        missingFields.form5.push(error.replace('Form5: ', ''));
      } else if (error.startsWith('Form6:')) {
        missingFields.form6.push(error.replace('Form6: ', ''));
      }
    });
    
    return missingFields;
  };

  // Проверка собирает карточку целиком (правила МП — по данным, уходящим на сервер),
  // поэтому пересчитываем её только при изменении данных, а не на каждой перерисовке.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const missingFields = useMemo(getMissingRequiredFields, [formsData, independentForm2Data, handbooks, dataType, selectedForm45]);
  const hasMissingRequiredFields = Object.values(missingFields).some(arr => arr.length > 0);

  useEffect(() => {
    const isCardOpen = !showDocumentsPage && !!selectedDoc;
    const shouldOpenPanel = isCardOpen && hasMissingRequiredFields && !prevHasMissingRequiredFieldsRef.current;

    if (shouldOpenPanel) {
      setIsRequiredFieldsPanelCollapsed(false);
    }

    prevHasMissingRequiredFieldsRef.current = hasMissingRequiredFields;
  }, [showDocumentsPage, selectedDoc, hasMissingRequiredFields]);

  // Синхронизируем внутреннюю навигацию приложения с историей браузера
  useEffect(() => {
    if (!authenticated) {
      historyInitializedRef.current = false;
      isHandlingPopstateRef.current = false;
      return;
    }

    const currentUrl = `${location.pathname}${location.search}${location.hash}`;
    const screen = showDocumentsPage ? 'documents' : 'form';

    if (!historyInitializedRef.current) {
      window.history.replaceState({ __glpm: true, screen }, '', currentUrl);
      window.history.pushState({ __glpm: true, screen: 'buffer' }, '', currentUrl);
      historyInitializedRef.current = true;
      return;
    }

    if (!isHandlingPopstateRef.current) {
      window.history.replaceState({ __glpm: true, screen }, '', currentUrl);
    }

    isHandlingPopstateRef.current = false;
  }, [authenticated, showDocumentsPage, location.pathname, location.search, location.hash]);

  // При нажатии "Назад" всегда возвращаем пользователя на главную страницу сайта
  useEffect(() => {
    if (!authenticated) {
      return undefined;
    }

    const handlePopState = () => {
      isHandlingPopstateRef.current = true;
      setSelectedDoc(null);
      setShowDocumentsPage(true);
      navigate('/', { replace: true });

      const currentUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;
      window.history.pushState({ __glpm: true, screen: 'buffer' }, '', currentUrl);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [authenticated, setSelectedDoc, navigate]);

  // Переносит текущие данные формы 4/5 в formsData и загружает данные целевой формы.
  // Общая логика для ручного переключения (с предупреждением) и автоматического
  // (жёсткого) выбора по цели ВНН.
  const applyForm45Switch = (targetForm) => {
    if (targetForm === 4) {
      // Сохраняем данные формы 5 перед переключением
      setFormsData(prev => ({
        ...prev,
        form5: formData
      }));
      // Загружаем данные формы 4 и очищаем модальные данные
      const form4Data = formsData.form4 || getEmptyForm4Data();
      const cleanForm4Data = {
        ...form4Data,
        savedModalData: {} // Очищаем модальные данные при переключении с формы 5
      };
      setFormData(cleanForm4Data);
      setSelectedForm45(4);
    } else if (targetForm === 5) {
      // Сохраняем данные формы 4 перед переключением
      setFormsData(prev => ({
        ...prev,
        form4: formData
      }));
      // Загружаем данные формы 5
      const form5Data = formsData.form5 || getEmptyForm5Data();
      setFormData(form5Data);
      setSelectedForm45(5);
    }
  };

  // Название выбранной цели ВНН (form1.cel, справочник 19) в нижнем регистре.
  // form1.cel может быть как простым id (во время редактирования), так и { handbookId, id }
  // (в загруженной с сервера карточке) — refToId приводит оба варианта к id.
  const resolveVnnGoalName = (celValue, handbooksList) => {
    const celId = refToId(celValue);
    if (!celId) return '';
    const handbook = (handbooksList || []).find(h => h.id === 19);
    const goal = handbook?.references?.find(r => String(r.id) === celId);
    return (goal?.name || '').toLowerCase();
  };

  // Жёсткая привязка формы 4/5 к цели ВНН (form1.cel): если цель содержит только
  // "санитарное" — форма 4, только "лесопатологическое" — форма 5, переключатель
  // при этом блокируется. Если цель охватывает оба варианта (или не задана) —
  // выбор формы остаётся свободным.
  useEffect(() => {
    const celValue = formsData.form1?.cel;
    const goalName = resolveVnnGoalName(celValue, handbooks);
    const hasSanitary = goalName.includes('санитар');
    const hasPatholog = goalName.includes('лесопатол');

    if (!refToId(celValue) || (hasSanitary && hasPatholog) || (!hasSanitary && !hasPatholog)) {
      setForm45Locked(false);
      return;
    }

    const targetForm = hasPatholog ? 5 : 4;
    setForm45Locked(true);
    if (selectedForm45 !== targetForm) {
      if (selectedForm === 4) {
        // Сейчас отображается форма 4/5 — переносим её данные, как при ручном переключении
        applyForm45Switch(targetForm);
      } else {
        setSelectedForm45(targetForm);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formsData.form1?.cel, handbooks, selectedForm, selectedForm45]);

  // Показываем лоадер, пока проверяем авторизацию
  if (isCheckingAuth) {
    return (
      <div style={{ height: '100vh' }}>
        <TreeLoader fill label="Загрузка…" />
      </div>
    );
  }

  // Показываем панель авторизации, если не авторизован
  if (!authenticated) {
    return <LoginPanel onLoginSuccess={handleLoginSuccess} />;
  }
  
  // Выбор документа
  const handleSelectDocument = (doc) => {
    setSelectedDoc(doc);
    // Не устанавливаем formData здесь - Form1 сам загрузит данные из localStorage
    // setFormData(doc.data || {});
  };

  // Функция создания нового документа
  const handleCreateDocument = () => {
    // Очищаем все данные форм перед созданием нового документа
    const emptyFormsData = {
      form1: getEmptyForm1Data(),
      form2: {
        to: getEmptyForm2Data(),
        actual: getEmptyForm2Data()
      },
      form3: getEmptyForm3Data(),
      form4: getEmptyForm4Data(),
      form5: getEmptyForm5Data(),
      form6: getEmptyForm6Data()
    };
    setFormsData(emptyFormsData);
    
    // Очищаем независимые данные формы 2
    const emptyIndependentForm2Data = {
      yarusy: [{
        id: 1,
        yarus: '1',
        sostav: ''
      }],
      poroda_characteristics: [],
      yarusy_dependent: [{
        id: 1,
        yarus: '1',
        vysota: '',
        klass_vozrasta: '',
        gruppa_vozrasta: '',
        polnota: '',
        zapas_syro_ga: '',
        zapas_syro_vydel: '',
        zapas_suhostoy: '',
        zapas_redin: '',
        zapas_ed_derev: '',
        zapas_zah_obsh: '',
        zapas_zah_likvid: ''
      }],
      poroda_characteristics_dependent: []
    };
    setIndependentForm2Data(emptyIndependentForm2Data);
    
    // Сбрасываем formData в пустые данные формы 1
    const emptyForm1Data = getEmptyForm1Data();
    setFormData(emptyForm1Data);
    
    // Сбрасываем состояние форм
    setSelectedForm(1);
    setDataType('to');
    setSelectedForm45(4);
    
    // Создаем новый документ
    // НЕ устанавливаем id, чтобы при сохранении шёл POST запрос на создание, а не PUT на обновление
    const newDoc = {
      name: 'Новый документ',
      createdAt: new Date().toLocaleString()
      // НЕ устанавливаем id - он будет создан на сервере при сохранении
    };
    // Используем setSelectedDoc напрямую, чтобы не создавать временный ID
    // Явно удаляем id, если он есть (например, из предыдущей сессии)
    const newDocWithoutId = { ...newDoc };
    delete newDocWithoutId.id;
    setSelectedDoc(newDocWithoutId);
  };

  // Обработчики для страницы документов
  const handleDocumentSelect = (doc) => {
    // Открытие существующей карточки — это не сессия создания
    setIsNewCardSession(false);
    // Сохраняем id документа с сервера, чтобы не потерять его при обработке
    const serverId = doc?.id;

    // API v2: version — настоящее поле properties.version, приходит явно на doc.version
    // (см. DocumentsPage.js handleRowClick), никакого разбора JSON-содержимого не нужно.
    let documentVersion = doc?.version ?? null;
    
    // Если документ содержит данные с сервера (имеет поле data), загружаем их в формы
    if (doc && doc.data) {
      try {
        const loadedData = loadDocumentDataFromServer(doc, handbooks);
        // Восстанавливаем режим карточки: он определяет площадь и веса ярусов
        // в формах 4–6. Режим предыдущей карточки здесь использовать нельзя.
        setDataType(loadedData.form2?.to?.useActualData ? 'actual' : 'to');
        
        // Загружаем данные в формы
        setFormsData(prev => ({
          form1: loadedData.form1 || prev.form1,
          form2: loadedData.form2 || prev.form2,
          form3: loadedData.form3 || prev.form3,
          form4: loadedData.form4 || prev.form4,
          form5: loadedData.form5 || prev.form5,
          form6: loadedData.form6 || prev.form6
        }));
        
        // Загружаем независимые данные формы 2
        if (loadedData.independentForm2Data) {
          setIndependentForm2Data(loadedData.independentForm2Data);
        }
        
        // Устанавливаем выбранную форму 4 или 5 ПЕРЕД проверкой условий
        let targetSelectedForm = selectedForm;
        if (loadedData.selectedForm45) {
          setSelectedForm45(loadedData.selectedForm45);
          // Если есть selectedForm45, значит это форма 4 или 5, устанавливаем selectedForm = 4
          targetSelectedForm = 4;
          setSelectedForm(4);
        }
        
        // Загружаем данные в formData в зависимости от текущей выбранной формы
        // ВАЖНО: сохраняем selectedOchags и ochagData при загрузке документа
        setFormData(prev => {
          const preservedData = {
            selectedOchags: prev?.selectedOchags,
            ochagData: prev?.ochagData
          };
          
          let newFormData;
          // Для форм 1-3 загружаем соответствующие данные
          // Для форм 4-5 загружаем данные, если форма уже выбрана, иначе они будут загружены при переключении
          if (loadedData.selectedForm45 === 5 && loadedData.form5) {
            // Приоритет: если selectedForm45 === 5, загружаем Form5
            newFormData = loadedData.form5;
            // ВАЖНО: сохраняем form6 с selectedOchagCodes и vidvo для Form5
            if (loadedData.form6) {
              newFormData.form6 = loadedData.form6;
            }
          } else if (loadedData.selectedForm45 === 4 && loadedData.form4) {
            // Если selectedForm45 === 4, загружаем Form4
            newFormData = loadedData.form4;
            // ВАЖНО: сохраняем form6 с selectedOchagCodes и vidvo для Form4
            if (loadedData.form6) {
              newFormData.form6 = loadedData.form6;
            }
          } else if (targetSelectedForm === 1 && loadedData.form1) {
            newFormData = loadedData.form1;
          } else if (targetSelectedForm === 3 && loadedData.form3) {
            newFormData = loadedData.form3;
          } else if (targetSelectedForm === 6 && loadedData.form6) {
            // Если выбрана форма 6, загружаем данные формы 6
            newFormData = { form6: loadedData.form6 };
          } else if (loadedData.form1) {
            // Fallback: если форма не 1, 3, 4, 5 или 6, загружаем form1
            newFormData = loadedData.form1;
          } else {
            newFormData = prev || {};
          }
          
          // Сохраняем selectedOchags и ochagData, если они есть
          return {
            ...newFormData,
            ...(preservedData.selectedOchags !== undefined && { selectedOchags: preservedData.selectedOchags }),
            ...(preservedData.ochagData !== undefined && { ochagData: preservedData.ochagData })
          };
        });
        
        // Обновляем название документа, если оно загружено, но сохраняем id с сервера
        if (loadedData.documentName) {
          doc = { ...doc, id: serverId, name: loadedData.documentName };
        } else {
          // Убеждаемся, что id сохранён
          doc = { ...doc, id: serverId };
        }
      } catch (error) {
        console.error('Ошибка при загрузке данных документа:', error);
        // В случае ошибки всё равно сохраняем id
        doc = { ...doc, id: serverId };
      }
    } else {
      // Если данных нет, но есть название в doc.data.name, используем его
      if (doc && doc.data && doc.data.name) {
        doc = { ...doc, id: serverId, name: doc.data.name };
      } else {
        // Убеждаемся, что id сохранён
        doc = { ...doc, id: serverId };
      }
    }
    
    // При открытии существующего отчёта сохраняем version — сервер сверит его при
    // следующем PUT (защита от конфликта, если документ открыт в нескольких вкладках).
    if (serverId) {
      if (documentVersion === null || documentVersion === undefined) {
        documentVersion = 1;
      }
      doc = { ...doc, version: documentVersion };
    }
    
    handleSelectDocument(doc);
    setEditSessionId(id => id + 1);
    setShowDocumentsPage(false);
  };

  const handleDocumentCreate = () => {
    handleCreateDocument();
    setIsNewCardSession(true);
    setEditSessionId(id => id + 1);
    setShowDocumentsPage(false);
  };

  // Функция восстановления данных из черновика
  const handleRestoreDraft = () => {
    try {
      const draft = loadDraft();
      if (!draft || !draft.data) {
        console.error('Черновик не найден или пуст');
        return false;
      }

      // Данные черновика имеют структуру документа (результат createDocumentData)
      // Структура: { documentId, name, ownerId, schemaVersion, docversion, data: { optionalTab, form1, form2, ... } }
      const draftDocument = draft.data;
      
      // Преобразуем данные черновика в формат, который ожидает loadDocumentDataFromServer
      // loadDocumentDataFromServer ожидает структуру: { data: { data: { ... } } } или { data: { ... } }
      const serverDataFormat = {
        data: {
          data: draftDocument.data || draftDocument,
          name: draftDocument.name
        },
        name: draftDocument.name
      };

      // Загружаем данные из черновика
      const loadedData = loadDocumentDataFromServer(serverDataFormat, handbooks);
      
      // Загружаем данные в формы
      setFormsData(prev => ({
        form1: loadedData.form1 || prev.form1,
        form2: loadedData.form2 || prev.form2,
        form3: loadedData.form3 || prev.form3,
        form4: loadedData.form4 || prev.form4,
        form5: loadedData.form5 || prev.form5,
        form6: loadedData.form6 || prev.form6
      }));
      
      // Загружаем независимые данные формы 2
      if (loadedData.independentForm2Data) {
        setIndependentForm2Data(loadedData.independentForm2Data);
      }
      
      // Устанавливаем dataType для формы 2 на основе useActualData
      if (loadedData.form2) {
        const form2Data = loadedData.form2.to || loadedData.form2.actual;
        if (form2Data && form2Data.useActualData !== undefined) {
          setDataType(form2Data.useActualData ? 'actual' : 'to');
        }
      }
      
      // Устанавливаем выбранную форму 4 или 5
      if (loadedData.selectedForm45) {
        setSelectedForm45(loadedData.selectedForm45);
        setSelectedForm(4);
      } else {
        setSelectedForm(1);
      }
      
      // Загружаем данные в formData в зависимости от выбранной формы
      if (loadedData.selectedForm45 === 5 && loadedData.form5) {
        setFormData(loadedData.form5);
        if (loadedData.form6) {
          setFormData(prev => ({ ...prev, form6: loadedData.form6 }));
        }
      } else if (loadedData.selectedForm45 === 4 && loadedData.form4) {
        setFormData(loadedData.form4);
        if (loadedData.form6) {
          setFormData(prev => ({ ...prev, form6: loadedData.form6 }));
        }
      } else if (loadedData.form1) {
        setFormData(loadedData.form1);
      }
      
      // Создаем новый документ (без id, чтобы это был новый документ)
      const newDoc = {
        name: loadedData.documentName || draftDocument.name || 'Новый документ',
        createdAt: new Date().toLocaleString()
      };
      const newDocWithoutId = { ...newDoc };
      delete newDocWithoutId.id;
      setSelectedDoc(newDocWithoutId);
      setEditSessionId(id => id + 1);

      // Открываем форму заполнения
      setShowDocumentsPage(false);
      return true;
    } catch (error) {
      console.error('Ошибка при восстановлении черновика:', error);
      return false;
    }
  };

  // Есть ли несохранённые изменения в открытой карточке
  const hasUnsavedCardChanges = () => {
    if (isViewOnlyReport) {
      return false;
    }
    if (showDocumentsPage || !cardInteractedRef.current || cardBaselineRef.current == null) {
      return false;
    }
    return getCardSnapshot() !== cardBaselineRef.current;
  };

  // Фактический выход в журнал карточек
  const performExitToJournal = () => {
    cardInteractedRef.current = false;
    cardBaselineRef.current = null;
    setSelectedDoc(null);
    setShowDocumentsPage(true);
    navigate('/', { replace: false });
  };

  // Журнал карточек ВНН — список на корневом URL (с подтверждением при несохранённых изменениях)
  const handleOpenJournalCards = () => {
    if (hasUnsavedCardChanges()) {
      setShowExitConfirm(true);
      return;
    }
    performExitToJournal();
  };

  // Отчёты — отдельная страница /reports
  const handleOpenReportsMenu = () => {
    setSelectedDoc(null);
    setShowDocumentsPage(true);
    navigate('/reports');
  };

  const handleOpenPlansMenu = () => {
    setSelectedDoc(null);
    setShowDocumentsPage(true);
    navigate('/plans');
  };

  const topNavButtonStyle = (active = false) => ({
    height: '42px',
    paddingLeft: '20px',
    paddingRight: '20px',
    fontSize: '0.95rem',
    fontWeight: active ? '600' : '500',
    borderRadius: '6px',
    border: active ? '1px solid #6c757d' : '1px solid #ddd',
    backgroundColor: active ? '#6c757d' : '#f8f9fa',
    color: active ? '#fff' : '#495057',
    transition: 'all 0.2s ease',
    whiteSpace: 'nowrap'
  });

  const handleTopNavMouseEnter = (e, active = false) => {
    e.currentTarget.style.backgroundColor = active ? '#5a6268' : '#e8ecef';
    e.currentTarget.style.borderColor = active ? '#5a6268' : '#adb5bd';
  };

  const handleTopNavMouseLeave = (e, active = false) => {
    e.currentTarget.style.backgroundColor = active ? '#6c757d' : '#f8f9fa';
    e.currentTarget.style.borderColor = active ? '#6c757d' : '#ddd';
  };

  const documentsPageSharedProps = {
    onSelectDocument: (doc) => {
      handleDocumentSelect(doc);
      navigate('/');
    },
    onCreateDocument: () => {
      handleDocumentCreate();
      navigate('/');
    },
    onRestoreDraft: () => {
      if (handleRestoreDraft()) {
        navigate('/');
      }
    },
    user,
    onLogout: handleLogout,
    handbooks
  };

  // Права доступа к страницам «Отчёты» и «Планы».
  // Смотрим фактически выданные permissions из авторизации: если право не пришло,
  // соответствующая верхняя кнопка не отображается. «Отчёты» — с уровня 1 (ему
  // доступны только отчёты о выполнении), «Планы» — уровни 2 и 3.
  const userPermissions = Array.isArray(user?.permissions) ? user.permissions : [];
  const isLevel2Or3 = user?.role === 'LEVEL2' || user?.role === 'LEVEL3';
  const hasReportsPermission = userPermissions.includes('CREATE_REPORTS');
  const hasPlansPermission = userPermissions.includes('MANAGE_PLANS');
  // LEVEL4 (администратор) работает с отчётами по всем субъектам, но не с планами
  const canCreateReports = user?.role === 'LEVEL4'
    || ((isLevel2Or3 || user?.role === 'LEVEL1') && hasReportsPermission);
  const canManagePlans = isLevel2Or3 && hasPlansPermission;

  // LEVEL4 — администратор: управление пользователями
  const isAdminLevel4 = user?.role === 'LEVEL4';
  // API v2: единый approvalState.status (NONE/PENDING/APPROVED/REJECTED) вместо
  // пары is_checked/user_approved.
  const approvalStatus = selectedDoc?.approvalStatus || 'NONE';
  // Режим «только просмотр». canEdit приходит с сервера (report_permissions.can_edit_report,
  // см. backend/catalogs/report_permissions.py) при открытии/сохранении существующего
  // документа — единый источник правды вместо повторного пересчёта роль×статус на клиенте
  // (тот же предикат используется и в проверке прав на PUT). LEVEL3 может редактировать
  // только свои карточки, чужие — нет; LEVEL4 не редактирует никогда. Запасной локальный
  // вариант — только для ещё не сохранённого нового документа (canEdit === undefined),
  // который по определению принадлежит текущему пользователю — LEVEL3 в нём не блокируем.
  const serverCanEdit = selectedDoc?.canEdit;
  const isViewOnlyReport = serverCanEdit !== undefined
    ? !serverCanEdit
    : (isAdminLevel4
      || (user?.role === 'LEVEL1' && (approvalStatus === 'PENDING' || approvalStatus === 'APPROVED'))
      || (user?.role === 'LEVEL2' && approvalStatus === 'APPROVED'));

  // Текстовый статус этапа карточки, показывается в шапке рядом с наименованием документа.
  // Этап зависит от approvalState.status, режим — от того, может ли именно текущий
  // пользователь редактировать карточку на этом этапе (isViewOnlyReport).
  const cardStageLabel = approvalStatus === 'APPROVED'
    ? 'Карточка одобрена пользователем и филиалом'
    : approvalStatus === 'REJECTED'
      ? 'Карточка отклонена филиалом'
      : approvalStatus === 'PENDING'
        ? 'Карточка одобрена пользователем'
        : 'Карточка в работе';
  const cardStatusLabel = `${cardStageLabel} (${isViewOnlyReport ? 'режим просмотра' : 'режим редактирования'})`;

  // Функции для обработки переключения форм 4/5 с предупреждением
  const handleForm45Switch = (newFormNumber) => {
    if (form45Locked) {
      return; // Выбор жёстко определён целью ВНН — ручное переключение недоступно
    }
    if (newFormNumber !== selectedForm45) {
      setPendingFormSwitch(newFormNumber);
      setShowFormSwitchWarning(true);
    }
  };

  const confirmFormSwitch = () => {
    applyForm45Switch(pendingFormSwitch);
    setShowFormSwitchWarning(false);
    setPendingFormSwitch(null);
  };

  const cancelFormSwitch = () => {
    setShowFormSwitchWarning(false);
    setPendingFormSwitch(null);
  };

  // Функции для обработки подтверждения смены типа данных
  const handleDataTypeChange = (newDataType) => {
    if (newDataType !== dataType) {
      // Сразу применяем изменения без модального окна
      setDataType(newDataType);
      
      // Сохраняем текущие данные формы 2 перед сменой типа данных
      setFormsData(prev => {
        const newFormsData = { ...prev };
        // Сохраняем текущие данные в старый тип данных (исключаем независимые таблицы и их зависимые данные)
        const { yarusy, poroda_characteristics, yarusy_dependent, poroda_characteristics_dependent, ...dependentFormData } = formData;
        newFormsData.form2[dataType] = dependentFormData;
        
        // Загружаем данные для нового типа данных
        const savedData = newFormsData.form2[newDataType] || {};
        
        // Объединяем зависимые данные с независимыми таблицами
        const combinedFormData = {
          ...savedData,
          yarusy: independentForm2Data.yarusy,
          poroda_characteristics: independentForm2Data.poroda_characteristics,
          // Используем независимые зависимые данные для таблиц
          yarusy_dependent: independentForm2Data.yarusy_dependent,
          poroda_characteristics_dependent: independentForm2Data.poroda_characteristics_dependent
        };
        
        setFormData(combinedFormData);
        
        return newFormsData;
      });
    }
  };

   // Обработчик переключения формы
   const handleFormChange = (formNumber, skipDuplicateCheck = false) => {
    if (selectedForm === 1 && formNumber !== 1) {
      const currentSgaRaw = String(formData?.sga ?? formsData?.form1?.sga ?? '').trim();
      const currentSga = parseFloat(currentSgaRaw);
      if (!currentSgaRaw || Number.isNaN(currentSga) || currentSga <= 0) {
        setFormsData(prev => ({
          ...prev,
          form1: formData
        }));
        setIsRequiredFieldsPanelCollapsed(false);
        setBlockedFormMessage(`Вы не можете перейти в форму №${formNumber}, т.к. в форме №1 не заполнено поле S (га).`);
        setShowFormBlockedModal(true);
        return;
      }
    }

    // При переходе с формы №1 на форму №2 (после всех проверок) — ищем карточку-дубликат
    // в рамках своей организации. Пока идёт запрос — показываем затемнение и лоадер.
    // Для администраторов (LEVEL3/LEVEL4) проверка дубликатов отключена.
    const isAdminRole = user?.role === 'LEVEL3' || user?.role === 'LEVEL4';
    if (selectedForm === 1 && formNumber === 2 && !skipDuplicateCheck && !isAdminRole) {
      (async () => {
        setShowForm1DuplicateLoader(true);
        try {
          const transformed = transformForm1(formData || {}, handbooks);
          // Исключаем текущую карточку, чтобы она не находила саму себя.
          // На сервере id открытой карточки хранится в selectedDoc.id (UUID).
          const isUuid = (id) => typeof id === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id);
          const excludeId = (selectedDoc?.id && isUuid(selectedDoc.id))
            ? selectedDoc.id
            : (selectedDoc?.documentId || formData?.documentId || formsData?.form1?.documentId || '');
          const token = getAccessToken();
          const response = await fetch(`${API_BASE}/reports/check-duplicate/`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(token ? getAuthHeaders() : {}),
            },
            body: JSON.stringify({ form1: transformed, excludeId }),
          });
          const data = await response.json().catch(() => ({}));
          setShowForm1DuplicateLoader(false);
          if (response.ok && data && data.exists) {
            // Сохраняем введённые данные формы №1 и показываем предупреждение
            setFormsData(prev => ({ ...prev, form1: formData }));
            setForm1DuplicateWarning(data.match || true);
            return;
          }
        } catch (e) {
          // При ошибке проверки не блокируем работу пользователя — пропускаем дальше
          setShowForm1DuplicateLoader(false);
          console.error('Ошибка проверки дубликата карточки:', e);
        }
        // Дубликата нет (или проверка не удалась) — продолжаем переход
        handleFormChange(2, true);
      })();
      return;
    }

    if (selectedForm === 2 && formNumber > 2) {
      const currentYarusyDependent = Array.isArray(independentForm2Data?.yarusy_dependent) && independentForm2Data.yarusy_dependent.length > 0
        ? independentForm2Data.yarusy_dependent
        : (Array.isArray(formData?.yarusy_dependent) ? formData.yarusy_dependent : []);
      // Таблица ярусов обязательна только на землях, покрытых лесной растительностью
      const katzem = formData?.katzem ?? formsData?.form2?.[dataType]?.katzem;
      const tiersOptional = isNonForestLandCategory(katzem, handbooks);
      const hasInvalidZapas = !tiersOptional && currentYarusyDependent.some(yarus => {
        const valueRaw = String(yarus?.zapassirga ?? '').trim();
        const value = parseFloat(valueRaw);
        return !valueRaw || Number.isNaN(value) || value <= 0;
      });

      if (hasInvalidZapas) {
        setFormsData(prev => ({
          ...prev,
          form2: {
            ...prev.form2,
            [dataType]: {
              ...prev.form2[dataType],
              ...(formData || {})
            }
          }
        }));
        setIsRequiredFieldsPanelCollapsed(false);
        setBlockedFormMessage(`Вы не можете перейти на форму №${formNumber}, т.к. в форме №2 поле «Запас сырораст., кбм на га» должно быть больше 0 для всех ярусов.`);
        setShowFormBlockedModal(true);
        return;
      }
    }

     // Сохраняем текущие данные формы и загружаем новые в одном обновлении
     setFormsData(prev => {
       const newFormsData = { ...prev };
       
       // Сохраняем текущие данные формы
       if (selectedForm === 1) {
         newFormsData.form1 = formData;
       } else if (selectedForm === 2) {
         // Для формы 2 сохраняем данные в соответствующий тип данных
         // ИСКЛЮЧАЕМ независимые таблицы и их зависимые данные
         const { yarusy, poroda_characteristics, yarusy_dependent, poroda_characteristics_dependent, ...dependentFormData } = formData;
         newFormsData.form2[dataType] = dependentFormData;
       } else if (selectedForm === 3) {
         // Для формы 3 сохраняем данные
         newFormsData.form3 = formData;
       } else if (selectedForm === 4) {
         // Для формы 4 сохраняем данные
         newFormsData.form4 = formData;
       } else if (selectedForm === 5) {
         // Для формы 5 сохраняем данные
         newFormsData.form5 = formData;
       } else if (selectedForm === 6) {
         // Для формы 6 сохраняем данные
         if (formData.form6) {
           newFormsData.form6 = formData.form6;
         }
       }
       
       // Загружаем данные новой формы
       let newFormData;
       if (formNumber === 1) {
         newFormData = newFormsData.form1 || getEmptyForm1Data();
         // Убеждаемся, что валидация формы 1 активна, а формы 2 - нет
         newFormData.form1ValidationErrors = newFormData.form1ValidationErrors !== undefined ? newFormData.form1ValidationErrors : true;
         newFormData.form2ValidationErrors = false; // Форма 2 не активна
       } else if (formNumber === 2) {
         // Для формы 2 загружаем данные в зависимости от текущего типа данных
         let dependentData = newFormsData.form2[dataType] || getEmptyForm2Data();
         
        // Зависимые данные для таблиц теперь независимы и не нужно их инициализировать
         
         // Объединяем зависимые данные с независимыми таблицами
         newFormData = {
           ...dependentData,
           yarusy: independentForm2Data.yarusy,
           poroda_characteristics: independentForm2Data.poroda_characteristics,
           // Используем независимые зависимые данные для таблиц
           yarusy_dependent: independentForm2Data.yarusy_dependent,
           poroda_characteristics_dependent: independentForm2Data.poroda_characteristics_dependent
         };
         
         // Убеждаемся, что валидация формы 2 активна, а формы 1 - нет
         newFormData.form2ValidationErrors = newFormData.form2ValidationErrors !== undefined ? newFormData.form2ValidationErrors : true;
         newFormData.form1ValidationErrors = false; // Форма 1 не активна
       } else if (formNumber === 3) {
         // Для формы 3 загружаем данные
         newFormData = newFormsData.form3 || getEmptyForm3Data();
         // Убеждаемся, что валидация формы 3 активна, а других форм - нет
         newFormData.form3ValidationErrors = newFormData.form3ValidationErrors !== undefined ? newFormData.form3ValidationErrors : true;
         newFormData.form1ValidationErrors = false; // Форма 1 не активна
         newFormData.form2ValidationErrors = false; // Форма 2 не активна
      } else if (formNumber === 4) {
        // Главная вкладка "4/5" — загружаем данные той из форм 4/5, что выбрана
        // текущим selectedForm45 (важно, когда выбор жёстко определён целью ВНН).
        if (selectedForm45 === 5) {
          newFormData = newFormsData.form5 || getEmptyForm5Data();
          newFormData.form5ValidationErrors = newFormData.form5ValidationErrors !== undefined ? newFormData.form5ValidationErrors : false;
          newFormData.form4ValidationErrors = false; // Форма 4 не активна
        } else {
          newFormData = newFormsData.form4 || getEmptyForm4Data();
          newFormData.form4ValidationErrors = newFormData.form4ValidationErrors !== undefined ? newFormData.form4ValidationErrors : false;
          newFormData.form5ValidationErrors = false; // Форма 5 не активна
        }
        newFormData.form1ValidationErrors = false; // Форма 1 не активна
        newFormData.form2ValidationErrors = false; // Форма 2 не активна
        newFormData.form3ValidationErrors = false; // Форма 3 не активна
      } else if (formNumber === 5) {
        // Для формы 5 загружаем данные
        newFormData = newFormsData.form5 || getEmptyForm5Data();
        // Убеждаемся, что валидация формы 5 активна, а других форм - нет
        newFormData.form5ValidationErrors = newFormData.form5ValidationErrors !== undefined ? newFormData.form5ValidationErrors : false;
        newFormData.form1ValidationErrors = false; // Форма 1 не активна
        newFormData.form2ValidationErrors = false; // Форма 2 не активна
        newFormData.form3ValidationErrors = false; // Форма 3 не активна
        newFormData.form4ValidationErrors = false; // Форма 4 не активна
      } else if (formNumber === 6) {
        // Для формы 6 загружаем данные
        // ВАЖНО: сохраняем selectedOchags и ochagData при переключении
        const focusForm = selectedForm45 === 5 ? newFormsData.form5 : newFormsData.form4;
        const preservedData = {
          selectedOchags: focusForm?.selectedOchags,
          ochagData: focusForm?.ochagData
        };
        newFormData = {
          form6: newFormsData.form6 || getEmptyForm6Data(),
          ...(preservedData.selectedOchags !== undefined && { selectedOchags: preservedData.selectedOchags }),
          ...(preservedData.ochagData !== undefined && { ochagData: preservedData.ochagData })
        };
        // Убеждаемся, что валидация формы 6 активна, а других форм - нет
        newFormData.form6ValidationErrors = newFormData.form6.form6ValidationErrors !== undefined ? newFormData.form6.form6ValidationErrors : false;
        newFormData.form1ValidationErrors = false; // Форма 1 не активна
        newFormData.form2ValidationErrors = false; // Форма 2 не активна
        newFormData.form3ValidationErrors = false; // Форма 3 не активна
        newFormData.form4ValidationErrors = false; // Форма 4 не активна
        newFormData.form5ValidationErrors = false; // Форма 5 не активна
      } else {
         newFormData = newFormsData[`form${formNumber}`] || {};
         // Для других форм отключаем валидацию
         newFormData.form1ValidationErrors = false;
         newFormData.form2ValidationErrors = false;
         newFormData.form3ValidationErrors = false;
         newFormData.form4ValidationErrors = false;
       }
       
       setFormData(newFormData);
       return newFormsData;
     });
     
    // Переключаемся на новую форму
    setSelectedForm(formNumber);
  };

  // Обработчик сохранения документа.
  // silent=true — тихое автосохранение: без спиннера, тостов и раскрытия панели обязательных полей.
  const handleSave = async (shouldExitAfterSave = false, { silent = false } = {}) => {
    // Проверенную карточку LEVEL1 и любые карточки LEVEL4 сохранять нельзя — только просмотр
    if (isViewOnlyReport) {
      if (shouldExitAfterSave) {
        setSelectedDoc(null);
        setShowDocumentsPage(true);
        navigate('/');
      }
      return;
    }
    // Не допускаем параллельных сохранений (ручное + автосейв)
    if (isSavingRef.current) {
      return;
    }
    isSavingRef.current = true;
    // Снимок содержимого, которое реально уходит на сервер (заполняется из updatedFormsData
    // ниже). Станет «последним сохранённым» при успехе, чтобы автосейв не зациклился.
    let savedSnapshot = null;
    if (!silent) {
      setIsSaving(true);
      setSaveError(null);
      setSaveSuccess(false);
    }

    try {
      // Сохраняем данные текущей формы перед сохранением
      let updatedFormsData = { ...formsData };
      
      // Сохраняем данные текущей активной формы
      if (selectedForm === 1) {
        // Если мы на форме 1, обновляем данные Form1 из текущего formData
        updatedFormsData.form1 = formData;
      } else if (selectedForm === 2) {
        // Для формы 2 сохраняем данные в соответствующий тип данных
        const { yarusy, poroda_characteristics, yarusy_dependent, poroda_characteristics_dependent, ...dependentFormData } = formData;
        updatedFormsData.form2 = {
          ...updatedFormsData.form2,
          [dataType]: dependentFormData
        };
        // Данные Form1 уже должны быть в formsData.form1 (сохранены при переключении)
        // Не перезаписываем их данными формы 2
        // Важно: сохраняем данные формы 4/5 из formsData, чтобы они не потерялись
        if (formsData.form4) {
          updatedFormsData.form4 = formsData.form4;
        }
        if (formsData.form5) {
          updatedFormsData.form5 = formsData.form5;
        }
      } else if (selectedForm === 3) {
        // Если мы на форме 3, обновляем данные Form3 из текущего formData
        updatedFormsData.form3 = formData;
      } else if (selectedForm === 4) {
        updatedFormsData.form4 = formData;
      } else if (selectedForm === 5) {
        updatedFormsData.form5 = formData;
      } else if (selectedForm === 6) {
        // Для формы 6 данные хранятся в formData.form6
        if (formData.form6) {
          updatedFormsData.form6 = formData.form6;
        }
      }
      
      // Если данные Form1 не сохранены (например, пользователь сразу перешел на форму 2),
      // но formData содержит поля Form1, используем их
      if (!updatedFormsData.form1 || Object.keys(updatedFormsData.form1).length === 0) {
        // Проверяем, содержит ли formData поля Form1 (например, srf, filial, lvo)
        if (formData.srf !== undefined || formData.filial !== undefined || formData.lvo !== undefined) {
          updatedFormsData.form1 = formData;
        }
      }
      
      // Аналогично для Form3 - если данные не сохранены, но formData содержит поля Form3, используем их
      if (!updatedFormsData.form3 || Object.keys(updatedFormsData.form3).length === 0) {
        // Проверяем, содержит ли formData поля Form3 (например, sostavpod, sostpod, reljeff)
        if (formData.sostavpod !== undefined || formData.sostpod !== undefined || formData.reljeff !== undefined) {
          updatedFormsData.form3 = formData;
        }
      }
      
      // Снимок отправляемого состояния — в том же формате, что и getCardSnapshot,
      // чтобы после успешного сохранения автосейв видел «изменений нет».
      try {
        savedSnapshot = JSON.stringify({
          formsData: updatedFormsData,
          independentForm2Data,
          selectedForm45,
          dataType,
        });
      } catch (e) {
        savedSnapshot = null;
      }

      // Валидация всех форм
      setFormsData(updatedFormsData);

      const validation = validateAllForms(updatedFormsData, independentForm2Data, handbooks, dataType, selectedForm45);

      if (!validation.isValid && !silent) {
        setIsRequiredFieldsPanelCollapsed(false);
      }
      
      // Получаем ID пользователя
      const userId = user?.id || 3; // По умолчанию 3, если нет данных пользователя
      
      // Логируем справочники перед созданием документа
      
      // Создаем документ (передаем handbooks для получения полных данных из справочников)
      // Используем имя из selectedDoc, если оно есть
      const documentName = (typeof selectedDoc.name === 'string' && selectedDoc.name.trim() !== '') 
        ? selectedDoc.name 
        : null;
      const document = createDocumentData(updatedFormsData, independentForm2Data, selectedForm45, userId, handbooks, documentName, dataType);
      
      // Логируем созданный документ для отладки
      
      // Отправляем на сервер
      // Если selectedDoc имеет id (UUID на сервере), используем обновление, иначе создание
      // Проверяем, является ли id валидным UUID (формат: xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx)
      // Если id - это просто число (временный ID), игнорируем его
      const isValidUUID = (id) => {
        if (!id) return false;
        const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
        return uuidRegex.test(id.toString());
      };
      
      const reportId = (selectedDoc?.id && isValidUUID(selectedDoc.id)) ? selectedDoc.id : null;
      // Версия, известная клиенту с момента открытия документа — сервер сверит её
      // с актуальной (защита от конфликта, если документ открыт в нескольких вкладках).
      const versionFromOpened = selectedDoc?.version ?? null;

      const result = await saveDocument(document, reportId, null, versionFromOpened);

      if (result.success) {
        // Удаляем черновик при успешном сохранении
        deleteDraft();

        // Фиксируем «последнее сохранённое» состояние — автосейв не сработает,
        // пока пользователь не внесёт новые изменения после этого момента.
        if (savedSnapshot != null) {
          lastSavedSnapshotRef.current = savedSnapshot;
        }

        if (!silent) {
          setSaveSuccess(true);
          setTimeout(() => setSaveSuccess(false), 3000);
        }

        // Версия/статус — то, что реально вернул сервер (API v2), а не пересчитанные
        // на клиенте предположения.
        setSelectedDoc(prev => prev ? ({
          ...prev,
          id: result.reportId || prev.id,
          version: result.version ?? prev.version,
          approvalStatus: result.approvalState?.status || prev.approvalStatus,
          canEdit: result.canEdit !== undefined ? result.canEdit : prev.canEdit,
        }) : prev);

        if (shouldExitAfterSave) {
          setSelectedDoc(null);
          setShowDocumentsPage(true);
          navigate('/');
        }
      } else if (!silent) {
        setSaveError(result.error || 'Ошибка при сохранении документа');
      }
    } catch (error) {
      if (!silent) {
        setSaveError(error.message || 'Неизвестная ошибка');
      }
    } finally {
      isSavingRef.current = false;
      if (!silent) {
        setIsSaving(false);
      }
    }
  };

  // Тик автосохранения. Переопределяется на каждый рендер, чтобы держать свежее
  // замыкание (handleSave, getCardSnapshot, selectedDoc и т.п.).
  autosaveTickRef.current = () => {
    if (isSavingRef.current) return;                       // уже идёт сохранение
    if (showDocumentsPage || isViewOnlyReport) return;     // не в режиме редактирования карточки
    if (!cardInteractedRef.current) return;                // пользователь ещё не вносил правок
    if (lastSavedSnapshotRef.current == null) return;      // базовая линия ещё не зафиксирована
    if (getCardSnapshot() === lastSavedSnapshotRef.current) return; // изменений после последнего сохранения нет
    handleSave(false, { silent: true });
  };

  // Изменение имени документа
  const handleNameChange = (e) => {
    const name = e.target.value;
    if (selectedDoc) {
      updateDocument(selectedDoc.id, { name });
    }
  };

  return (
    <Routes>
      <Route
        path="/reports"
        element={
          canCreateReports ? (
            <div style={{ width: '100vw', height: '100vh' }}>
              <DocumentsPage
                {...documentsPageSharedProps}
                listHeading="Меню работы с отчётами"
                showJournalTable={false}
                canCreateReports={canCreateReports}
                canManagePlans={canManagePlans}
              />
            </div>
          ) : (
            <Navigate to="/" replace />
          )
        }
      />
      <Route
        path="/plans"
        element={
          canManagePlans ? (
            <div style={{ width: '100vw', height: '100vh' }}>
              <PlansPage
                user={user}
                onLogout={handleLogout}
                canCreateReports={canCreateReports}
                canManagePlans={canManagePlans}
              />
            </div>
          ) : (
            <Navigate to="/" replace />
          )
        }
      />
      <Route
        path="/users"
        element={
          isAdminLevel4 ? (
            <div style={{ width: '100vw', height: '100vh' }}>
              <DocumentsPage
                {...documentsPageSharedProps}
                listHeading="Модерация пользователей"
                adminUsersMode
                canCreateReports={canCreateReports}
                canManagePlans={canManagePlans}
              />
            </div>
          ) : (
            <Navigate to="/" replace />
          )
        }
      />
      <Route
        path="*"
        element={
          showDocumentsPage ? (
            <div style={{ width: '100vw', height: '100vh' }}>
              <DocumentsPage
                {...documentsPageSharedProps}
                canCreateReports={canCreateReports}
                canManagePlans={canManagePlans}
              />
            </div>
          ) : (
    <div
      style={{ width: '100vw', height: '100vh', display: 'flex', flexDirection: 'column' }}
      onPointerDownCapture={() => { cardInteractedRef.current = true; }}
      onKeyDownCapture={() => { cardInteractedRef.current = true; }}
    >
      {/* Глобальный хедер */}
      <div className="top-menu mlr-0-8 bb-1 row space-between" style={{ 
        height: '5.5vh', 
        minHeight: '55px', 
        paddingTop: '8px', 
        paddingBottom: '8px',
        paddingLeft: '1.5vw',
        paddingRight: '1.5vw',
        alignItems: 'center'
      }}>
        {/* Логотип, подпись и кнопки слева */}
        <div style={{ display: 'flex', gap: '16px', alignItems: 'center', height: '100%', flex: '0 0 auto' }}>
          {/* Логотип */}
          <div style={{ display: 'flex', alignItems: 'center', height: '100%', flexShrink: 0 }}>
            <img 
              src={logoImage} 
              alt="Рослес" 
              style={{ 
                height: '38px', 
                width: 'auto',
                objectFit: 'contain',
                maxHeight: '100%'
              }} 
            />
          </div>
          
          {/* Подпись ГЛПМ */}
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            height: '100%',
            flexShrink: 0
          }}>
            <span style={{ 
              fontSize: '1rem',
              fontWeight: '600',
              color: '#495057',
              letterSpacing: '0.5px'
            }}>
              ГЛПМ
            </span>
          </div>
          
          {/* Кнопки на большем расстоянии вправо */}
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', height: '100%', marginLeft: '40px' }}>
            <Button
              size="m"
              mode="outline"
              appearance="neutral"
              style={topNavButtonStyle(false)}
              onMouseEnter={(e) => handleTopNavMouseEnter(e, false)}
              onMouseLeave={(e) => handleTopNavMouseLeave(e, false)}
              onClick={handleOpenJournalCards}
            >
              Журнал карточек ВНН
            </Button>
            <Button
              size="m"
              mode="outline"
              appearance="neutral"
              style={topNavButtonStyle(false)}
              onMouseEnter={(e) => handleTopNavMouseEnter(e, false)}
              onMouseLeave={(e) => handleTopNavMouseLeave(e, false)}
              onClick={() => {
                window.open(USER_GUIDE_URL, '_blank');
              }}
            >
              Руководство пользователя
            </Button>
            {canCreateReports && (
              <Button
                size="m"
                mode="outline"
                appearance="neutral"
                style={topNavButtonStyle(false)}
                onMouseEnter={(e) => handleTopNavMouseEnter(e, false)}
                onMouseLeave={(e) => handleTopNavMouseLeave(e, false)}
                onClick={handleOpenReportsMenu}
              >
                Отчёты
              </Button>
            )}
            {canManagePlans && (
              <Button
                size="m"
                mode="outline"
                appearance="neutral"
                style={topNavButtonStyle(false)}
                onMouseEnter={(e) => handleTopNavMouseEnter(e, false)}
                onMouseLeave={(e) => handleTopNavMouseLeave(e, false)}
              onClick={handleOpenPlansMenu}
            >
                Планы
              </Button>
            )}
          </div>
        </div>
        
        {/* Информация об аккаунте справа */}
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', height: '100%', flex: '0 0 auto' }}>
          {user && (
            <>
              <div style={{ 
                height: '32px', 
                display: 'flex', 
                alignItems: 'center',
                paddingLeft: '16px',
                paddingRight: '16px',
                borderLeft: '1px solid #e0e0e0',
                marginLeft: '8px'
              }}>
                <span className="sz-0-8 b-5" style={{ 
                  color: '#495057', 
                  margin: 0, 
                  padding: 0, 
                  lineHeight: '1.2',
                  whiteSpace: 'nowrap'
                }}>
                  {user.username}
                </span>
              </div>
              <Button
                size="m"
                mode="outline"
                appearance="neutral"
                style={{ 
                  height: '42px',
                  paddingLeft: '20px',
                  paddingRight: '20px',
                  fontSize: '0.95rem',
                  fontWeight: '500',
                  borderRadius: '6px',
                  border: '1px solid #ddd',
                  backgroundColor: '#f8f9fa',
                  color: '#495057',
                  transition: 'all 0.2s ease',
                  whiteSpace: 'nowrap'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#e8ecef';
                  e.currentTarget.style.borderColor = '#adb5bd';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#f8f9fa';
                  e.currentTarget.style.borderColor = '#ddd';
                }}
                onClick={handleLogout}
              >
                Выход
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="app" style={{ display: 'flex', flex: 1, overflow: 'hidden', position: 'relative' }}>
        {/* Sidebar */}
        <div 
          className="sidebar" 
          style={{ 
            overflowY: 'auto',
            width: isRequiredFieldsPanelCollapsed ? '0' : '25vw',
            minWidth: isRequiredFieldsPanelCollapsed ? '0' : '250px',
            maxWidth: isRequiredFieldsPanelCollapsed ? '0' : '25vw',
            borderRight: isRequiredFieldsPanelCollapsed ? 'none' : '1px solid rgba(0, 0, 0, 0.1)',
            transition: 'width 0.3s ease, min-width 0.3s ease, max-width 0.3s ease, border 0.3s ease',
            overflow: isRequiredFieldsPanelCollapsed ? 'hidden' : 'auto',
            position: 'relative'
          }}
        >
          <div 
            className="top-menu mlr-0-8 bb-1 row" 
            style={{ 
              height: '5vh', 
              minHeight: '48px',
              userSelect: 'none'
            }}
          >
            <p className="sz-1-3 mt0 b-7" style={{ margin: 0, flex: 1 }}>Замечания</p>
          </div>
          {!isRequiredFieldsPanelCollapsed && (
          <div className="main-menu mlr-0-8" style={{ paddingTop: '10px', paddingBottom: '20px' }}>
            {missingFields.form1.length > 0 && (
              <div style={{
                marginBottom: '12px'
              }}>
                <div style={{
                  backgroundColor: 'transparent',
                  borderBottom: '1px solid #dee2e6',
                  padding: '10px 12px'
                }}>
                  <p className="sz-0-8 b-5" style={{ margin: 0, color: '#000', fontWeight: '600' }}>Форма 1</p>
                </div>
                <div style={{ padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {missingFields.form1.map((field, index) => (
                    <div key={index} style={{
                      backgroundColor: 'transparent',
                      borderRadius: '6px',
                      border: '1px solid #6c757d',
                      padding: '8px 10px',
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                    }}>
                      <p className="sz-0-7 b-3 mt0" style={{ margin: 0, color: '#495057', lineHeight: '1.4' }}>
                        {field}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {missingFields.form2.length > 0 && (
              <div style={{
                marginBottom: '12px'
              }}>
                <div style={{
                  backgroundColor: 'transparent',
                  borderBottom: '1px solid #dee2e6',
                  padding: '10px 12px'
                }}>
                  <p className="sz-0-8 b-5" style={{ margin: 0, color: '#000', fontWeight: '600' }}>Форма 2</p>
                </div>
                <div style={{ padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {missingFields.form2.map((field, index) => (
                    <div key={index} style={{
                      backgroundColor: 'transparent',
                      borderRadius: '6px',
                      border: '1px solid #6c757d',
                      padding: '8px 10px',
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                    }}>
                      <p className="sz-0-7 b-3 mt0" style={{ margin: 0, color: '#495057', lineHeight: '1.4' }}>
                        {field}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {missingFields.form3.length > 0 && (
              <div style={{
                marginBottom: '12px'
              }}>
                <div style={{
                  backgroundColor: 'transparent',
                  borderBottom: '1px solid #dee2e6',
                  padding: '10px 12px'
                }}>
                  <p className="sz-0-8 b-5" style={{ margin: 0, color: '#000', fontWeight: '600' }}>Форма 3</p>
                </div>
                <div style={{ padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {missingFields.form3.map((field, index) => (
                    <div key={index} style={{
                      backgroundColor: 'transparent',
                      borderRadius: '6px',
                      border: '1px solid #6c757d',
                      padding: '8px 10px',
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                    }}>
                      <p className="sz-0-7 b-3 mt0" style={{ margin: 0, color: '#495057', lineHeight: '1.4' }}>
                        {field}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {missingFields.form4.length > 0 && (
              <div style={{
                marginBottom: '12px'
              }}>
                <div style={{
                  backgroundColor: 'transparent',
                  borderBottom: '1px solid #dee2e6',
                  padding: '10px 12px'
                }}>
                  <p className="sz-0-8 b-5" style={{ margin: 0, color: '#000', fontWeight: '600' }}>Форма 4</p>
                </div>
                <div style={{ padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {missingFields.form4.map((field, index) => (
                    <div key={index} style={{
                      backgroundColor: 'transparent',
                      borderRadius: '6px',
                      border: '1px solid #6c757d',
                      padding: '8px 10px',
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                    }}>
                      <p className="sz-0-7 b-3 mt0" style={{ margin: 0, color: '#495057', lineHeight: '1.4' }}>
                        {field}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {missingFields.form5.length > 0 && (
              <div style={{
                marginBottom: '12px'
              }}>
                <div style={{
                  backgroundColor: 'transparent',
                  borderBottom: '1px solid #dee2e6',
                  padding: '10px 12px'
                }}>
                  <p className="sz-0-8 b-5" style={{ margin: 0, color: '#000', fontWeight: '600' }}>Форма 5</p>
                </div>
                <div style={{ padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {missingFields.form5.map((field, index) => (
                    <div key={index} style={{
                      backgroundColor: 'transparent',
                      borderRadius: '6px',
                      border: '1px solid #6c757d',
                      padding: '8px 10px',
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                    }}>
                      <p className="sz-0-7 b-3 mt0" style={{ margin: 0, color: '#495057', lineHeight: '1.4' }}>
                        {field}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {missingFields.form6.length > 0 && (
              <div style={{
                marginBottom: '12px'
              }}>
                <div style={{
                  backgroundColor: 'transparent',
                  borderBottom: '1px solid #dee2e6',
                  padding: '10px 12px'
                }}>
                  <p className="sz-0-8 b-5" style={{ margin: 0, color: '#000', fontWeight: '600' }}>Форма 6</p>
                </div>
                <div style={{ padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {missingFields.form6.map((field, index) => (
                    <div key={index} style={{
                      backgroundColor: 'transparent',
                      borderRadius: '6px',
                      border: '1px solid #6c757d',
                      padding: '8px 10px',
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                    }}>
                      <p className="sz-0-7 b-3 mt0" style={{ margin: 0, color: '#495057', lineHeight: '1.4' }}>
                        {field}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {Object.values(missingFields).every(arr => arr.length === 0) && (
              <div style={{
                backgroundColor: 'transparent',
                borderRadius: '8px',
                border: '1px solid #6c757d',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                padding: '16px 12px',
                textAlign: 'center'
              }}>
                <p className="sz-0-8 b-5 mt0" style={{ margin: 0, color: '#000' }}>
                  ✓ Замечаний нет
                </p>
              </div>
            )}
          </div>
          )}
        </div>

      {/* Кнопка для сворачивания/разворачивания панели (всегда видна) */}
      <div
        style={{
          position: 'absolute',
          left: isRequiredFieldsPanelCollapsed ? '0' : '25vw',
          top: '50%',
          transform: 'translateY(-50%)',
          width: '30px',
          height: '60px',
          backgroundColor: '#f5f5f5',
          border: '1px solid rgba(0, 0, 0, 0.1)',
          borderLeft: isRequiredFieldsPanelCollapsed ? 'none' : '1px solid rgba(0, 0, 0, 0.1)',
          borderRight: isRequiredFieldsPanelCollapsed ? '1px solid rgba(0, 0, 0, 0.1)' : 'none',
          borderTopRightRadius: isRequiredFieldsPanelCollapsed ? '8px' : '0',
          borderBottomRightRadius: isRequiredFieldsPanelCollapsed ? '8px' : '0',
          borderTopLeftRadius: isRequiredFieldsPanelCollapsed ? '0' : '8px',
          borderBottomLeftRadius: isRequiredFieldsPanelCollapsed ? '0' : '8px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          boxShadow: isRequiredFieldsPanelCollapsed ? '2px 0 4px rgba(0, 0, 0, 0.1)' : '-2px 0 4px rgba(0, 0, 0, 0.1)',
          transition: 'all 0.3s ease',
          marginLeft: isRequiredFieldsPanelCollapsed ? '0' : '-30px'
        }}
        onClick={() => setIsRequiredFieldsPanelCollapsed(!isRequiredFieldsPanelCollapsed)}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = '#e9ecef';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = '#f5f5f5';
        }}
      >
        <span style={{ 
          fontSize: '16px', 
          fontWeight: 'bold',
          transition: 'transform 0.2s'
        }}>
          {isRequiredFieldsPanelCollapsed ? '▶' : '◀'}
        </span>
      </div>

      {/* Правая часть */}
      <div 
        className="forms" 
        style={{ 
          flexGrow: 1, 
          display: 'flex', 
          flexDirection: 'column', 
          overflow: 'hidden',
          width: isRequiredFieldsPanelCollapsed ? '100%' : '73vw',
          transition: 'width 0.3s ease'
        }}
      >
        <div style={{ flex: 1, padding: '20px', overflowY: 'auto' }}>
        {isLoading ? (
          // Лоадер пока справочники загружаются
          <TreeLoader fill label="Загрузка справочников…" />
        ) : error ? (
          // Ошибка загрузки
          <div
            style={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#e74c3c',
              fontSize: '1.2rem'
            }}
          >
            <div
              style={{
                width: '60px',
                height: '60px',
                border: '4px solid #f3f3f3',
                borderTop: '4px solid #e74c3c',
                borderRadius: '50%',
                marginBottom: '20px'
              }}
            />
            <p>Ошибка загрузки справочников</p>
            <p style={{ fontSize: '0.9rem', color: '#999', marginTop: '10px', textAlign: 'center' }}>
              {error}
            </p>
            <Button
              size="m"
              mode="primary"
              appearance="neutral"
              style={{ marginTop: '20px' }}
              onClick={() => loadHandbooks(true)}
            >
              Попробовать снова
            </Button>
          </div>
        ) : selectedDoc ? (
          <>
            {/* Редактирование имени и кнопка сохранения */}
            <div style={{ marginBottom: '15px' }}>
              <div className="top-menu mlr-0-8 row" style={{ alignItems: 'center', gap: '12px' }}>
                <p className="sz-0-8 b-3 mt0" style={{ margin: 0, whiteSpace: 'nowrap' }}>Наименование документа:</p>
                <Input
                  id="name-document"
                  type="text"
                  style={{ width: '30vw', flex: '0 0 auto' }}
                  value={typeof selectedDoc.name === 'string' ? selectedDoc.name : ''}
                  onChange={handleNameChange}
                  readOnly={isViewOnlyReport}
                />
                {!isViewOnlyReport && (
                <div style={{ display: 'flex', gap: '12px', marginLeft: 'auto' }}>
                  <button
                    onClick={() => handleSave(false)}
                    disabled={isSaving}
                    style={{
                      padding: '10px 20px',
                      backgroundColor: '#6c757d',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: isSaving ? 'not-allowed' : 'pointer',
                      fontSize: '14px',
                      fontWeight: '500',
                      minWidth: '120px',
                      opacity: isSaving ? 0.6 : 1
                    }}
                    onMouseOver={(e) => {
                      if (!isSaving) {
                        e.target.style.backgroundColor = '#5a6268';
                      }
                    }}
                    onMouseOut={(e) => {
                      if (!isSaving) {
                        e.target.style.backgroundColor = '#6c757d';
                      }
                    }}
                  >
                    {isSaving ? 'Сохранение...' : 'Сохранить'}
                  </button>
                  <button
                    onClick={() => handleSave(true)}
                    disabled={isSaving}
                    style={{
                      padding: '10px 20px',
                      backgroundColor: '#495057',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: isSaving ? 'not-allowed' : 'pointer',
                      fontSize: '14px',
                      fontWeight: '500',
                      minWidth: '170px',
                      opacity: isSaving ? 0.6 : 1
                    }}
                    onMouseOver={(e) => {
                      if (!isSaving) {
                        e.target.style.backgroundColor = '#343a40';
                      }
                    }}
                    onMouseOut={(e) => {
                      if (!isSaving) {
                        e.target.style.backgroundColor = '#495057';
                      }
                    }}
                  >
                    {isSaving ? 'Сохранение...' : 'Сохранить и выйти'}
                  </button>
                </div>
                )}
              </div>
              <div className="mlr-0-8" style={{ marginTop: '8px' }}>
                {isViewOnlyReport ? (
                  <span style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: '8px 14px',
                    backgroundColor: '#e8f5e9',
                    border: '1px solid #a5d6a7',
                    borderRadius: '4px',
                    color: '#2e7d32',
                    fontSize: '14px',
                    fontWeight: '500',
                    whiteSpace: 'nowrap'
                  }}>
                    {cardStatusLabel}
                  </span>
                ) : (
                  <span style={{
                    color: '#6c757d',
                    fontSize: '14px',
                    fontWeight: '500',
                    whiteSpace: 'nowrap'
                  }}>
                    {cardStatusLabel}
                  </span>
                )}
              </div>
            </div>

            {/* Сообщения об ошибках и успехе */}
            {saveError && (
              <div style={{
                margin: '0 20px 15px 20px',
                padding: '12px',
                backgroundColor: '#f8d7da',
                border: '1px solid #f5c6cb',
                borderRadius: '4px',
                color: '#721c24'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                  <strong>Ошибка:</strong>
                  <button
                    onClick={() => setSaveError(null)}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#721c24',
                      cursor: 'pointer',
                      fontSize: '18px',
                      fontWeight: 'bold',
                      padding: '0',
                      marginLeft: '10px'
                    }}
                  >
                    ×
                  </button>
                </div>
                <div style={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  gap: '4px',
                  marginTop: '8px'
                }}>
                  {saveError.split('\n').filter(err => err.trim()).map((error, index) => (
                    <div key={index} style={{ display: 'flex', alignItems: 'center' }}>
                      <span style={{ marginRight: '8px' }}>•</span>
                      <span>{error.trim()}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {saveSuccess && (
              <div style={{
                margin: '0 20px 15px 20px',
                padding: '12px',
                backgroundColor: '#d4edda',
                border: '1px solid #c3e6cb',
                borderRadius: '4px',
                color: '#155724'
              }}>
                <strong>Успешно!</strong> Документ сохранен.
              </div>
            )}


            {/* Рендер форм */}
            <div className="main-menu mlr-0-8">
              <p className="sz-0-8 mlr-0-8 b-3 mt5">Формы:</p>
              
              {/* Выбор формы */}
              <div className="form-selector">
                {[1, 2, 3, 4, 6].map(formNumber => (
        <button
                    key={formNumber}
                    className={`form-selector-btn ${selectedForm === formNumber ? 'active' : ''} ${formNumber === 1 && formData.form1ValidationErrors ? 'validation-error' : ''} ${formNumber === 2 && formData.form2ValidationErrors ? 'validation-error' : ''}`}
                onClick={() => handleFormChange(formNumber)}
                  >
                    {formNumber === 4 ? '4/5' : formNumber}
            </button>
          ))}
        </div>
              
              {/* Переключатель формы 4/5 */}
              {selectedForm === 4 && (
                <div className="form-45-selector-container" style={{ marginTop: '15px' }}>
                  <p className="sz-0-7 mlr-0-8 b-3 mt0" style={{ marginBottom: '8px' }}>Выберите форму:</p>
                  <div className="form-45-selector">
                    <button
                      className={`form-45-btn ${selectedForm45 === 4 ? 'active' : ''}`}
                      onClick={() => handleForm45Switch(4)}
                      disabled={form45Locked}
                      title={form45Locked ? 'Форма определена целью ВНН на форме №1' : undefined}
                      style={form45Locked ? { cursor: 'not-allowed', opacity: 0.6 } : undefined}
                    >
                      Форма №4
                    </button>
                    <button
                      className={`form-45-btn ${selectedForm45 === 5 ? 'active' : ''}`}
                      onClick={() => handleForm45Switch(5)}
                      disabled={form45Locked}
                      title={form45Locked ? 'Форма определена целью ВНН на форме №1' : undefined}
                      style={form45Locked ? { cursor: 'not-allowed', opacity: 0.6 } : undefined}
                    >
                      Форма №5
                    </button>
                  </div>
                  {form45Locked && (
                    <p className="sz-0-7 mlr-0-8 mt0" style={{ marginTop: '6px', color: '#6c757d' }}>
                      Форма выбрана автоматически по цели ВНН (форма №1)
                    </p>
                  )}
                </div>
              )}
              
              {/* Выбор типа данных - только для формы 2 */}
              {selectedForm === 2 && (
                <>
                  <div className="data-type-selector">
                    <button
                      className={`data-type-btn ${dataType === 'to' ? 'active' : ''}`}
                      onClick={() => handleDataTypeChange('to')}
                    >
                      Использовать материалы ТО
                    </button>
                    <button
                      className={`data-type-btn ${dataType === 'actual' ? 'active' : ''}`}
                      onClick={() => handleDataTypeChange('actual')}
                    >
                      Использовать фактические данные
                    </button>
                  </div>
                </>
              )}
        
              {/* Рендер выбранной формы */}
              {selectedForm === 1 && (
                <Form1
                handbooks={handbooks}
                formData={formData}
                setFormData={setFormData}
                  selectedDoc={selectedDoc}
                  user={user}
                  isNewCard={isNewCardSession}
                />
              )}
              
              {selectedForm === 2 && (
                <Form2
                  handbooks={handbooks}
                  formData={formData}
                  setFormData={setFormData}
                  selectedDoc={selectedDoc}
                  dataType={dataType}
                  independentData={independentForm2Data}
                  setIndependentData={setIndependentForm2Data}
                  form1Data={formsData.form1}
                  form4Data={formsData.form4}
                  form5Data={formsData.form5}
                />
              )}
              
              {selectedForm === 3 && (
                <Form3
                  handbooks={handbooks}
                  formData={formData}
                  setFormData={setFormData}
                  selectedDoc={selectedDoc}
                />
              )}
              
              {selectedForm === 4 && selectedForm45 === 4 && (
                <Form4
                  handbooks={handbooks}
                  formData={formData}
                  setFormData={setFormData}
                  selectedDoc={selectedDoc}
                  form2Data={formsData.form2}
                  independentForm2Data={independentForm2Data}
                  dataType={dataType}
                  form1Data={formsData.form1}
                  form6Data={formsData.form6}
                  isActive={selectedForm === 4 && selectedForm45 === 4}
                />
              )}
              
              {selectedForm === 4 && selectedForm45 === 5 && (
                <Form5
                  handbooks={handbooks}
                  formData={formData}
                  setFormData={setFormData}
                  selectedDoc={selectedDoc}
                  form2Data={formsData.form2}
                  independentForm2Data={independentForm2Data}
                  dataType={dataType}
                  form1Data={formsData.form1}
                  isActive={selectedForm === 4 && selectedForm45 === 5}
                  hasValidationErrors={formData.form5ValidationErrors}
                  setHasValidationErrors={(errors) => {
                    setFormData(prev => ({
                      ...prev,
                      form5ValidationErrors: errors
                    }));
                  }}
                />
              )}

              {selectedForm === 6 && (
                <Form6
                  handbooks={handbooks}
                  formData={formData}
                  setFormData={setFormData}
                  selectedDoc={selectedDoc}
                  form4Data={formsData.form4}
                  form5Data={formsData.form5}
                  form2Data={formsData.form2}
                  independentForm2Data={independentForm2Data}
                  dataType={dataType}
                  form1Data={formsData.form1}
                  isActive={selectedForm === 6}
                  selectedForm45={selectedForm45}
                  isNewCard={isNewCardSession}
                />
              )}

              {/* Дублированная навигация по формам внизу */}
              <div style={{ marginTop: '30px', padding: '20px', backgroundColor: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '6px' }}>
                <p className="sz-0-8 mlr-0-8 b-3 mt5" style={{ marginBottom: '15px' }}>Переключение форм:</p>
                
                {/* Выбор формы */}
                <div className="form-selector">
                  {[1, 2, 3, 4, 6].map(formNumber => (
                    <button
                      key={formNumber}
                      className={`form-selector-btn ${selectedForm === formNumber ? 'active' : ''} ${formNumber === 1 && formData.form1ValidationErrors ? 'validation-error' : ''} ${formNumber === 2 && formData.form2ValidationErrors ? 'validation-error' : ''}`}
                      onClick={() => handleFormChange(formNumber)}
                    >
                      {formNumber === 4 ? '4/5' : formNumber}
                    </button>
                  ))}
                </div>
              </div>
              
            </div>
          </>
        ) : (
          // Плейсхолдер, когда ничего не выбрано
          <div
            style={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#888',
              fontSize: '1.2rem'
            }}
          >
            Выберите документ для редактирования
          </div>
        )}
      </div>
      
      {/* Модальное окно предупреждения при переключении форм 4/5 */}
      {showFormBlockedModal && (
        <div className="modal-overlay" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div className="modal-content" style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '20px',
            maxWidth: '500px',
            width: '90%',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
          }}>
            <h3 style={{ margin: '0 0 15px 0', color: '#6c757d', fontSize: '16px', fontWeight: '600' }}>
              Переход недоступен
            </h3>
            <p style={{ margin: '0 0 20px 0', color: '#6c757d', lineHeight: '1.5', fontSize: '14px' }}>
              {blockedFormMessage}
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowFormBlockedModal(false);
                  setBlockedFormMessage('');
                }}
                style={{
                  padding: '10px 20px',
                  border: '1px solid #6c757d',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
                onMouseOver={(e) => {
                  e.target.style.backgroundColor = '#5a6268';
                }}
                onMouseOut={(e) => {
                  e.target.style.backgroundColor = '#6c757d';
                }}
              >
                Понятно
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Затемнение + лоадер на время проверки дубликата карточки */}
      {showForm1DuplicateLoader && (
        <div className="modal-overlay" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1200
        }}>
          <div className="modal-content" style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '12px 36px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
          }}>
            <TreeLoader label="Загрузка…" />
          </div>
        </div>
      )}

      {/* Предупреждение: найдена карточка-дубликат */}
      {form1DuplicateWarning && (
        <div className="modal-overlay" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1200
        }}>
          <div className="modal-content" style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '20px',
            maxWidth: '520px',
            width: '90%',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
          }}>
            <h3 style={{ margin: '0 0 15px 0', color: '#c0392b', fontSize: '16px', fontWeight: '600' }}>
              Возможный дубликат карточки
            </h3>
            <p style={{ margin: '0 0 12px 0', color: '#6c757d', lineHeight: '1.5', fontSize: '14px' }}>
              В вашей организации уже есть карточка с такими же субъектом РФ, организацией, лесничеством,
              участковым лесничеством, урочищем, кварталом, кадастровым номером и параметрами лесотаксационного
              и лесопатологического выделов, с той же датой проведения ВНН (по месяцу). Проверьте введённые данные.
            </p>
            {form1DuplicateWarning && typeof form1DuplicateWarning === 'object' && (
              <div style={{
                margin: '0 0 20px 0',
                padding: '12px 14px',
                background: '#f8f9fa',
                border: '1px solid #e0e0e0',
                borderRadius: '6px',
                fontSize: '13px',
                color: '#333',
                lineHeight: '1.6'
              }}>
                <div style={{ fontWeight: 600, marginBottom: '6px', color: '#6c757d' }}>Найденная карточка:</div>
                {form1DuplicateWarning.name && (<div><b>Наименование:</b> {form1DuplicateWarning.name}</div>)}
                {form1DuplicateWarning.address && (<div><b>Адрес:</b> {form1DuplicateWarning.address}</div>)}
                {form1DuplicateWarning.vnn_date && (<div><b>Дата проведения ВНН:</b> {form1DuplicateWarning.vnn_date}</div>)}
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button
                onClick={() => setForm1DuplicateWarning(false)}
                style={{
                  padding: '10px 20px',
                  border: '1px solid #6c757d',
                  backgroundColor: 'white',
                  color: '#6c757d',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                Изменить данные
              </button>
              <button
                onClick={() => {
                  setForm1DuplicateWarning(false);
                  handleFormChange(2, true);
                }}
                style={{
                  padding: '10px 20px',
                  border: '1px solid #6c757d',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
                onMouseOver={(e) => { e.target.style.backgroundColor = '#5a6268'; }}
                onMouseOut={(e) => { e.target.style.backgroundColor = '#6c757d'; }}
              >
                Всё равно продолжить
              </button>
            </div>
          </div>
        </div>
      )}

      {showExitConfirm && (
        <div className="modal-overlay" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1100
        }}>
          <div className="modal-content" style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '20px',
            maxWidth: '500px',
            width: '90%',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
          }}>
            <h3 style={{ margin: '0 0 15px 0', color: '#6c757d', fontSize: '16px', fontWeight: '600' }}>
              Сохранить изменения?
            </h3>
            <p style={{ margin: '0 0 20px 0', color: '#6c757d', lineHeight: '1.5', fontSize: '14px' }}>
              В карточке есть несохранённые изменения. Сохранить их перед выходом в журнал карточек?
            </p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => setShowExitConfirm(false)}
                style={{
                  padding: '10px 20px',
                  border: '1px solid #ced4da',
                  backgroundColor: '#fff',
                  color: '#495057',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                Отмена
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowExitConfirm(false);
                  performExitToJournal();
                }}
                style={{
                  padding: '10px 20px',
                  border: '1px solid #6c757d',
                  backgroundColor: '#f8f9fa',
                  color: '#6c757d',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                Не сохранять
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowExitConfirm(false);
                  handleSave(true);
                }}
                style={{
                  padding: '10px 20px',
                  border: '1px solid #495058',
                  backgroundColor: '#495058',
                  color: 'white',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                Сохранить
              </button>
            </div>
          </div>
        </div>
      )}

      {showFormSwitchWarning && (
        <div className="modal-overlay" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div className="modal-content" style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '20px',
            maxWidth: '500px',
            width: '90%',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
          }}>
            <h3 style={{ margin: '0 0 15px 0', color: '#6c757d', fontSize: '16px', fontWeight: '600' }}>
              Предупреждение
            </h3>
            <p style={{ margin: '0 0 20px 0', color: '#6c757d', lineHeight: '1.5', fontSize: '14px' }}>
              При переключении между формами №4 и №5 данные могут быть безвозвратно удалены. 
              Убедитесь, что вы сохранили все необходимые данные перед продолжением.
            </p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={cancelFormSwitch}
                style={{
                  padding: '10px 20px',
                  border: '1px solid #6c757d',
                  backgroundColor: '#f8f9fa',
                  color: '#6c757d',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
                onMouseOver={(e) => {
                  e.target.style.backgroundColor = '#e9ecef';
                  e.target.style.color = '#495057';
                }}
                onMouseOut={(e) => {
                  e.target.style.backgroundColor = '#f8f9fa';
                  e.target.style.color = '#6c757d';
                }}
              >
                Отмена
              </button>
              <button
                onClick={confirmFormSwitch}
                style={{
                  padding: '10px 20px',
                  border: '1px solid #6c757d',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
                onMouseOver={(e) => {
                  e.target.style.backgroundColor = '#5a6268';
                }}
                onMouseOut={(e) => {
                  e.target.style.backgroundColor = '#6c757d';
                }}
              >
                Продолжить
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
      </div>
    </div>
          )
        }
      />
    </Routes>
  );
}

export default App;
